import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { listStandards, listItemsByStandard } from '../../services/items'
import { emptyFilter, coerceFilter, makeMatchers } from './codeFilter'
import { deriveView } from './codeTree'
import { coerceHistory, pushHistory, stepHistory } from './locationHistory'
import { CODE_NAV_KEY, navForJump } from './openInCode'
import { loadJSON, saveJSON } from '../../utils/tabStorage'
import { standardId, standardName, remapStoredStandard } from '../../utils/standardRef'

const EMPTY_ITEMS = []

// One nav state per window: which standard is entered, the drill trail (as item
// `order`s below root), collapsed subtrees, view mode, the highlighted row, and
// the window's own filter. Fully serializable so the whole map persists.
function defaultNav() {
  return {
    mode: 'segmented',
    standard: null,
    trailOrders: [],
    collapsed: [],
    selectedId: null,
    open: null, // the item shown in this window's viewer (its own object)
    history: [], // sections visited in this window, oldest first
    historyIndex: -1, // pointer into history; forward of this is discarded on a new visit
    recall: null, // transient page jump from back/forward; not restored on reload
    filter: emptyFilter(),
  }
}

function coerceNav(raw) {
  const base = defaultNav()
  if (!raw || typeof raw !== 'object') return base
  const open = raw.open && typeof raw.open === 'object' ? raw.open : null
  return {
    mode: raw.mode === 'collapsed' ? 'collapsed' : 'segmented',
    standard: typeof raw.standard === 'string' ? raw.standard : null,
    trailOrders: Array.isArray(raw.trailOrders) ? raw.trailOrders : [],
    collapsed: Array.isArray(raw.collapsed) ? raw.collapsed : [],
    selectedId: raw.selectedId ?? null,
    open,
    ...coerceHistory(raw, open),
    recall: null,
    filter: coerceFilter(raw.filter),
  }
}

// The persisted map is { [windowId]: navState }. Reconciled against the grid's
// live window ids on every layout change (grid persists which ids exist + which
// is active), so on reload every window reopens exactly where it was left.
function loadNavMap() {
  try {
    const parsed = loadJSON(CODE_NAV_KEY)
    if (!parsed || typeof parsed !== 'object') return {}
    const out = {}
    Object.keys(parsed).forEach((id) => {
      out[id] = coerceNav(parsed[id])
    })
    return out
  } catch {
    return {}
  }
}

const clone = (nav) => JSON.parse(JSON.stringify(nav))

// All window ids in the grid (including minimized), in layout order.
function collectWindowIds(columns) {
  const ids = []
  columns.forEach((col) => col.windows.forEach((w) => ids.push(w.id)))
  return ids
}

// Owns the Library nav for the whole tab: a per-window map of nav states, an
// in-memory cache of each standard's item tree (so switching windows is
// instant), and the derived view for the *active* window. All browse/filter
// mutators target the active window only. `grid` is the useWindowGrid() result
// (source of window ids + which one is active); `bookmarks` is useBookmarks().
export default function useCodeWorkspace(grid, bookmarks, topics = []) {
  const activeId = grid.activeId

  const [navById, setNavById] = useState(loadNavMap)
  // When adding a window we capture the active nav here so the reconcile effect
  // can seed the new window as a copy of it (rather than a blank root state).
  const copySourceRef = useRef(null)
  // Bumped by revealItem so CodeNav scrolls the revealed row into view. `id` is
  // the target row's _id; `nonce` forces the scroll effect to re-run on repeats.
  const [revealSignal, setRevealSignal] = useState({ id: null, nonce: 0 })

  // Standards (level-0) list — loaded once, shared by every window's root view.
  const [standards, setStandards] = useState([])
  const [standardsStatus, setStandardsStatus] = useState('loading') // loading|ready|error

  // In-memory item-tree cache, keyed by standard name.
  const [itemsByStandard, setItemsByStandard] = useState({})
  const [itemsStatus, setItemsStatus] = useState({}) // standard -> loading|ready|error
  // Standards a fetch has already been kicked off for (dedupe). A ref so the
  // fetch effect can depend on `nav.standard` alone without re-running (and
  // cancelling its own in-flight request) each time it writes status.
  const requestedRef = useRef(new Set())

  // --- persistence -----------------------------------------------------------
  const timerRef = useRef(null)
  useEffect(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      try {
        saveJSON(CODE_NAV_KEY, navById)
      } catch {
        /* ignore quota / privacy-mode errors */
      }
    }, 150)
    return () => clearTimeout(timerRef.current)
  }, [navById])

  // --- reconcile the nav map with the grid's windows -------------------------
  // New id -> seed from the captured active copy (or a blank root state);
  // removed id -> drop its nav state.
  useEffect(() => {
    const ids = collectWindowIds(grid.columns)
    // Capture + clear the copy source synchronously: the setState updater runs
    // later (during the re-render), so reading the ref inside it would see the
    // value already cleared.
    const source = copySourceRef.current
    copySourceRef.current = null
    setNavById((prev) => {
      let changed = false
      const next = {}
      ids.forEach((id) => {
        if (prev[id]) {
          next[id] = prev[id]
        } else {
          next[id] = source ? clone(source) : defaultNav()
          changed = true
        }
      })
      // Detect drops (an id present before but gone now).
      if (!changed && Object.keys(prev).length !== ids.length) changed = true
      return changed ? next : prev
    })
  }, [grid.columns])

  const nav = navById[activeId] || defaultNav()

  // --- data loading ----------------------------------------------------------
  useEffect(() => {
    let alive = true
    listStandards()
      .then((list) => {
        if (!alive) return
        setStandards(list)
        setStandardsStatus('ready')
      })
      .catch(() => alive && setStandardsStatus('error'))
    return () => {
      alive = false
    }
  }, [])

  // Saved windows may still name a standard by its old string. Rewrite those to
  // the Standard id once the catalog is loaded.
  useEffect(() => {
    if (!standards.length) return undefined
    setNavById((prev) => {
      let changed = false
      const next = {}
      Object.keys(prev).forEach((id) => {
        const navState = prev[id]
        const mapped = remapStoredStandard(navState.standard, standards)
        const openMapped = navState.open
          ? remapStoredStandard(navState.open.standard, standards)
          : null
        const history = (navState.history || []).map((entry) => {
          const mappedEntry = remapStoredStandard(entry?.standard, standards)
          if (!mappedEntry) return entry
          if (
            standardId(entry.standard) === standardId(mappedEntry) &&
            standardName(entry.standard)
          ) {
            return entry
          }
          return { ...entry, standard: mappedEntry }
        })
        const standard = mapped ? standardId(mapped) : standardId(navState.standard) || null
        const open = navState.open
          ? { ...navState.open, standard: openMapped || navState.open.standard }
          : navState.open
        const historyChanged = history.some((entry, index) => entry !== navState.history?.[index])
        if (standard !== navState.standard || open?.standard !== navState.open?.standard || historyChanged) {
          changed = true
          next[id] = { ...navState, standard, open, history }
        } else {
          next[id] = navState
        }
      })
      return changed ? next : prev
    })
    return undefined
  }, [standards])

  // Every standard we need a tree for: the active window's browsed standard (for
  // the sidebar) plus each window's opened item's standard (for its viewer's
  // live location breadcrumb, which maps the scrolled page to a section).
  const neededStandards = useMemo(() => {
    const set = new Set()
    Object.values(navById).forEach((n) => {
      const current = standardId(n?.standard)
      const open = standardId(n?.open?.standard)
      if (current) set.add(current)
      if (open) set.add(open)
    })
    return [...set]
  }, [navById])
  const neededKey = neededStandards.join('|')

  // Fetch + cache each needed standard's tree once. Keyed by standard name via
  // `requestedRef`, so a status write can't re-trigger and cancel the fetch;
  // responses are stored by standard, so an out-of-order response is harmless.
  useEffect(() => {
    neededStandards.forEach((std) => {
      if (!/^[a-fA-F0-9]{24}$/.test(std)) return
      if (requestedRef.current.has(std)) return
      requestedRef.current.add(std)
      setItemsStatus((s) => ({ ...s, [std]: 'loading' }))
      listItemsByStandard(std)
        .then((list) => {
          setItemsByStandard((m) => ({ ...m, [std]: list }))
          setItemsStatus((s) => ({ ...s, [std]: 'ready' }))
        })
        .catch(() => {
          // Allow a later retry when the standard is revisited.
          requestedRef.current.delete(std)
          setItemsStatus((s) => ({ ...s, [std]: 'error' }))
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededKey])

  // The cached item tree for a standard (empty until it loads). Used by each
  // window's viewer to resolve its live location as the reader scrolls.
  const treeFor = useCallback((standard) => itemsByStandard[standardId(standard)] || EMPTY_ITEMS, [
    itemsByStandard,
  ])

  // --- derived view for the active window ------------------------------------
  const matchers = useMemo(
    () => makeMatchers(nav.filter, bookmarks, topics),
    [nav.filter, bookmarks, topics]
  )

  const items = itemsByStandard[standardId(nav.standard)] || []
  const view = useMemo(
    () => deriveView({ nav, items, standards, matchers }),
    [nav, items, standards, matchers]
  )

  const status = view.atRoot ? standardsStatus : itemsStatus[standardId(nav.standard)] || 'loading'

  const hasChildren = useCallback((item) => view.parentOrders.has(item.order), [view])
  const isExpanded = useCallback((item) => !view.collapsedSet.has(item.order), [view])
  const depthOf = useCallback(
    (item) => {
      if (view.atRoot || view.mode !== 'collapsed' || !view.currentNode) return 0
      return Math.max(0, item.level - view.currentNode.level - 1)
    },
    [view]
  )
  const isDimmed = useCallback(
    (item) => view.matchSet != null && !view.matchSet.has(item._id),
    [view]
  )

  // --- mutators (target the active window) -----------------------------------
  const patchNav = useCallback(
    (updater) => {
      setNavById((prev) => {
        const cur = prev[activeId] || defaultNav()
        const nextNav = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater }
        return { ...prev, [activeId]: nextNav }
      })
    },
    [activeId]
  )

  // Patch a specific window's nav (not necessarily the active one). Used by
  // revealItem, which targets the window whose viewer button was clicked (its
  // focus dispatch hasn't flipped `activeId` yet within the same event).
  const patchNavFor = useCallback((windowId, updater) => {
    setNavById((prev) => {
      const cur = prev[windowId] || defaultNav()
      const nextNav = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater }
      return { ...prev, [windowId]: nextNav }
    })
  }, [])

  // Open `item` in one window, including when it belongs to another standard.
  // The window loads that document on the item's page, and the sidebar drills
  // to the section once its outline is available.
  const pendingReveal = useRef(null)
  const openInWindow = useCallback(
    (windowId, item) => {
      if (!windowId || !item) return
      const id = standardId(item.standard)
      const known = standards.find((row) => standardId(row.standard) === id)
      const enriched = known
        ? { ...item, standard: { _id: id, name: known.label || standardName(known.standard) } }
        : item
      patchNavFor(windowId, (cur) => navForJump(cur, enriched))
      const selected = String(enriched._id || enriched.item || '')
      if (!selected || !id) return
      if (itemsByStandard[id]?.length) {
        setRevealSignal((signal) => ({ id: selected, nonce: signal.nonce + 1 }))
        return
      }
      pendingReveal.current = { id: selected, standard: id }
    },
    [patchNavFor, standards, itemsByStandard]
  )
  useEffect(() => {
    const pending = pendingReveal.current
    if (!pending) return undefined
    const status = itemsStatus[pending.standard]
    if (status !== 'ready' && status !== 'error') return undefined
    pendingReveal.current = null
    if (status === 'ready') {
      setRevealSignal((signal) => ({ id: pending.id, nonce: signal.nonce + 1 }))
    }
    return undefined
  }, [itemsStatus])

  // Reveal a window's live viewer section in the sidebar. Does not change the
  // opened item or the PDF. `trail` is root→current, level > 0.
  const revealItem = useCallback(
    (windowId, trail) => {
      if (!windowId || !trail?.length) return
      const current = trail[trail.length - 1]
      const ancestorOrders = trail.slice(0, -1).map((t) => t.order)
      patchNavFor(windowId, (cur) => {
        const collapsed = new Set(cur.collapsed)
        ancestorOrders.forEach((o) => collapsed.delete(o))
        return {
          ...cur,
          standard: standardId(current.standard) || cur.standard,
          // Segmented: drill to the parent so the section shows as a child row.
          // Collapsed: stay at root and just expand the ancestor chain.
          trailOrders: cur.mode === 'collapsed' ? [] : ancestorOrders,
          collapsed: [...collapsed],
          selectedId: current._id,
        }
      })
      setRevealSignal((s) => ({ id: current._id, nonce: s.nonce + 1 }))
    },
    [patchNavFor]
  )

  const setMode = useCallback((mode) => patchNav({ mode }), [patchNav])
  // Open a row in this window's viewer: store the whole item (so the viewer needs
  // no tree fetch) and highlight it in the sidebar. Works for a standard row at
  // root (full standard) or any item inside a standard (jumps to its page).
  const select = useCallback(
    (row) =>
      patchNav((cur) => {
        const next = { ...cur, open: row || null, selectedId: row?._id ?? null }
        return row ? pushHistory(next, row) : next
      }),
    [patchNav]
  )

  // Record a section the viewer landed on (a scroll that rested, or a click
  // in the location bar / a comment). Does not move the sidebar or the PDF.
  const visit = useCallback(
    (windowId, item) => patchNavFor(windowId, (cur) => pushHistory(cur, item)),
    [patchNavFor]
  )

  // Back (dir -1) or forward (dir 1) within one window's section history.
  const stepHistoryNav = useCallback(
    (windowId, dir) => patchNavFor(windowId, (cur) => stepHistory(cur, dir)),
    [patchNavFor]
  )

  const enterStandard = useCallback(
    (std) =>
      patchNav((cur) => ({ ...cur, standard: standardId(std.standard), trailOrders: [], collapsed: [] })),
    [patchNav]
  )
  const drillInto = useCallback(
    (item) => patchNav((cur) => ({ ...cur, trailOrders: [...cur.trailOrders, item.order] })),
    [patchNav]
  )
  // Breadcrumb: index -1 == Library (exit the standard), 0 == the standard root,
  // k>0 == the k-th drilled node.
  const goTo = useCallback(
    (index) => {
      if (index < 0) {
        patchNav((cur) => ({ ...cur, standard: null, trailOrders: [], collapsed: [] }))
        return
      }
      patchNav((cur) => ({ ...cur, trailOrders: cur.trailOrders.slice(0, index) }))
    },
    [patchNav]
  )
  const toggleCollapse = useCallback(
    (item) =>
      patchNav((cur) => {
        const set = new Set(cur.collapsed)
        if (set.has(item.order)) set.delete(item.order)
        else set.add(item.order)
        return { ...cur, collapsed: [...set] }
      }),
    [patchNav]
  )

  // Filter mutators (active window's filter).
  const setFacet = useCallback(
    (key, value) =>
      patchNav((cur) => ({
        ...cur,
        filter: { ...cur.filter, values: { ...cur.filter.values, [key]: value } },
      })),
    [patchNav]
  )
  const setText = useCallback(
    (text) => patchNav((cur) => ({ ...cur, filter: { ...cur.filter, text } })),
    [patchNav]
  )
  const setMatchMode = useCallback(
    (matchMode) => patchNav((cur) => ({ ...cur, filter: { ...cur.filter, matchMode } })),
    [patchNav]
  )
  const setBookmarkedOnly = useCallback(
    (bookmarkedOnly) =>
      patchNav((cur) => ({ ...cur, filter: { ...cur.filter, bookmarkedOnly } })),
    [patchNav]
  )
  const setIncludeTopicDescendants = useCallback(
    (includeTopicDescendants) =>
      patchNav((cur) => ({ ...cur, filter: { ...cur.filter, includeTopicDescendants } })),
    [patchNav]
  )
  const clearFilter = useCallback(
    () =>
      patchNav((cur) => ({
        ...cur,
        filter: { ...emptyFilter(), matchMode: cur.filter.matchMode },
      })),
    [patchNav]
  )

  // Call right before grid.addWindow so the new window is seeded as a copy of
  // the currently active window's nav (browse position + filter + mode).
  const prepareWindowCopy = useCallback(() => {
    copySourceRef.current = navById[activeId] ? clone(navById[activeId]) : defaultNav()
  }, [navById, activeId])

  return {
    // active window's derived view
    status,
    atRoot: view.atRoot,
    mode: view.mode,
    rows: view.rows,
    trail: view.trail,
    matchCount: view.matchCount,
    selectedId: nav.selectedId,
    hasChildren,
    isExpanded,
    depthOf,
    isDimmed,
    // browse actions
    setMode,
    select,
    visit,
    stepHistory: stepHistoryNav,
    enterStandard,
    drillInto,
    goTo,
    toggleCollapse,
    revealItem,
    revealSignal,
    openInWindow,
    // filter state + actions (active window)
    filter: { ...nav.filter, active: matchers.active },
    setFacet,
    setText,
    setMatchMode,
    setBookmarkedOnly,
    setIncludeTopicDescendants,
    clearFilter,
    // per-window resolution (window bodies + titles): each window's opened item
    navById,
    treeFor,
    // window lifecycle
    prepareWindowCopy,
  }
}
